/** Server-only Jev evaluation using separate native and AI Gateway contracts.
 * https://docs.typesafe.ai/api
 * https://vercel.com/docs/ai-gateway/modalities/evaluation
 * An unavailable evaluation is never a simulated or affirmative verdict.
 */
import { z } from 'zod';

export type JevQuestion =
  | { type: 'boolean'; instructions: string; criteria?: { true: string; false: string } }
  | { type: 'choice'; instructions: string; criteria: Record<string, string> }
  | { type: 'score'; instructions: string; criteria: string[] };

export type JevAnswer =
  | { type: 'boolean'; probability: number }
  | { type: 'choice'; choice: string; probabilities: Record<string, number>; confidence?: number }
  | { type: 'score'; score: number; probabilities: Record<string, number>; confidence?: number };

export type JevModel = 'typesafe-ai/jev' | 'jev-latest' | 'jev-preview' | `jev-${number}.${number}.${number}`;
export interface JevTarget {
  provider: 'typesafe-native' | 'vercel-ai-gateway';
  endpoint: string;
  requestedModel: JevModel;
}

export interface JevEvaluateOptions {
  model?: JevModel;
  state: Record<string, unknown> | unknown[] | string;
  questions: Record<string, JevQuestion>;
  /** Gateway routing control only. Native retention is an account policy, not a request flag. */
  zeroDataRetention?: boolean;
  signal?: AbortSignal;
}

export interface JevEvaluateResult {
  /** Actual provider-reported model; native aliases resolve to a versioned Jev model. */
  model: string;
  answers: Record<string, JevAnswer>;
}

const probability = z.number().finite().min(0).max(1);
const choiceAnswer = z.object({ type: z.literal('choice'), choice: z.string(), probabilities: z.record(z.string(), probability), confidence: probability.optional() });
const scoreAnswer = z.object({ type: z.literal('score'), score: z.number().finite(), probabilities: z.record(z.string(), probability), confidence: probability.optional() });
const gatewayAnswerSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('boolean'), probability }), choiceAnswer, scoreAnswer,
]);
const nativeAnswerSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('noul'), noul: probability }).strict(),
  choiceAnswer.extend({ confidence: probability }).strict(),
  scoreAnswer.extend({ confidence: probability, legend: z.record(z.string(), z.string()) }).strict(),
]);
const gatewayResultSchema = z.object({ model: z.string(), answers: z.record(z.string(), gatewayAnswerSchema) });
const nativeResultSchema = z.object({
  model: z.string(), answers: z.record(z.string(), nativeAnswerSchema),
  usage: z.object({ input_tokens: z.number().int().nonnegative(), output_tokens: z.number().int().nonnegative() }),
});
const instructions = z.string().trim().min(1);
const questionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('boolean'), instructions, criteria: z.object({ true: instructions, false: instructions }).optional() }).strict(),
  z.object({ type: z.literal('choice'), instructions, criteria: z.record(z.string().min(1), instructions).refine(x => Object.keys(x).length >= 2 && Object.keys(x).length <= 255) }).strict(),
  z.object({ type: z.literal('score'), instructions, criteria: z.array(instructions).min(2).max(10) }).strict(),
]);
const NATIVE_ENDPOINT = 'https://api.typesafe.ai/v1/systemone';
const GATEWAY_ENDPOINT = 'https://ai-gateway.vercel.sh/v1/evaluate';
const NATIVE_VERSION = /^jev-\d+\.\d+\.\d+$/;

/** Safe receipt metadata only: credentials never leave this module or cross providers. */
export function resolveJevTarget(model?: JevModel): JevTarget {
  const native = Boolean(process.env.JEV_API_KEY?.trim()) || !process.env.AI_GATEWAY_API_KEY?.trim();
  if (process.env.JEV_GATEWAY_URL && process.env.JEV_GATEWAY_URL !== GATEWAY_ENDPOINT) {
    throw new Error('Jev requires a documented evaluation endpoint.');
  }
  const requestedModel = model ?? (native ? 'jev-latest' : 'typesafe-ai/jev');
  if (native ? !(['jev-latest', 'jev-preview'].includes(requestedModel) || NATIVE_VERSION.test(requestedModel)) : requestedModel !== 'typesafe-ai/jev') {
    throw new Error('Unsupported Jev model for the configured provider.');
  }
  return { provider: native ? 'typesafe-native' : 'vercel-ai-gateway', endpoint: native ? NATIVE_ENDPOINT : GATEWAY_ENDPOINT, requestedModel };
}

/** Normalized boolean probability means P(yes/true), never confidence in a separate value. */
export async function evaluateJev(options: JevEvaluateOptions): Promise<JevEvaluateResult> {
  if (typeof window !== 'undefined') throw new Error('Jev evaluation requires a server context.');
  const target = resolveJevTarget(options.model);
  const native = target.provider === 'typesafe-native';
  const apiKey = (native ? process.env.JEV_API_KEY : process.env.AI_GATEWAY_API_KEY)?.trim();
  if (!apiKey) throw new Error('Jev evaluation is unavailable: provider credentials are not configured.');
  if (native && options.zeroDataRetention === true) {
    // Native ZDR is an enterprise account agreement; the HTTP contract has no request control.
    throw new Error('Jev native retention cannot be guaranteed by a request option.');
  }
  const questions = z.record(z.string().min(1), questionSchema).parse(options.questions);
  if (!Object.keys(questions).length) throw new Error('Jev requires at least one question.');
  const wireQuestions = native ? Object.fromEntries(Object.entries(questions).map(([id, q]) => [id, q.type === 'boolean' ? { ...q, type: 'noul' } : q])) : questions;
  const timeout = AbortSignal.timeout(10_000);
  const signal = options.signal ? AbortSignal.any([timeout, options.signal]) : timeout;
  let response: Response;
  try {
    signal.throwIfAborted();
    response = await fetch(target.endpoint, {
      method: 'POST', redirect: 'error', signal,
      headers: { Authorization: `Bearer ${apiKey}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ model: target.requestedModel, state: options.state, questions: wireQuestions,
        ...(!native ? { providerOptions: { gateway: { zeroDataRetention: options.zeroDataRetention !== false, only: ['typesafe-ai'] } } } : {}),
      }),
    });
  } catch {
    // Never log provider payloads, source text or credentials on this path.
    throw new Error('Jev evaluation is unavailable: request failed or timed out.');
  }
  if (!response.ok) throw new Error(`Jev evaluation is unavailable: HTTP ${response.status}.`);
  try {
    const raw = await response.json();
    const parsed = native ? nativeResultSchema.parse(raw) : gatewayResultSchema.parse(raw);
    const modelMatches = native && ['jev-latest', 'jev-preview'].includes(target.requestedModel)
      ? NATIVE_VERSION.test(parsed.model) : parsed.model === target.requestedModel;
    if (!modelMatches || Object.keys(parsed.answers).length !== Object.keys(questions).length) throw new Error();
    const normalized: Record<string, JevAnswer> = {};
    for (const [key, question] of Object.entries(questions)) {
      const answer = Object.hasOwn(parsed.answers, key) ? parsed.answers[key] : undefined;
      if (!answer) throw new Error();
      if (question.type === 'boolean') {
        if (native ? answer.type !== 'noul' : answer.type !== 'boolean') throw new Error();
        normalized[key] = { type: 'boolean', probability: answer.type === 'noul' ? answer.noul : (answer as Extract<JevAnswer, { type: 'boolean' }>).probability };
        continue;
      }
      if (answer.type !== question.type) throw new Error();
      const labels = question.type === 'choice' ? Object.keys(question.criteria) : question.criteria.map((_, i) => String(i));
      if (Object.keys(answer.probabilities).length !== labels.length || labels.some(label => !Object.hasOwn(answer.probabilities, label))) throw new Error();
      const values = Object.values(answer.probabilities);
      if (Math.abs(values.reduce((a, b) => a + b, 0) - 1) > 0.01) throw new Error();
      if (answer.type === 'choice' && (!labels.includes(answer.choice) || answer.probabilities[answer.choice] < Math.max(...values))) throw new Error();
      if (answer.type === 'score') {
        const weighted = labels.reduce((sum, label) => sum + Number(label) * answer.probabilities[label], 0);
        if (answer.score < 0 || answer.score > labels.length - 1 || Math.abs(answer.score - weighted) > 0.01) throw new Error();
        if (native) {
          if (question.type !== 'score') throw new Error();
          const legend = z.record(z.string(), z.string()).parse('legend' in answer ? answer.legend : undefined);
          if (Object.keys(legend).length !== labels.length || labels.some((label, i) => !Object.hasOwn(legend, label) || legend[label] !== question.criteria[i])) throw new Error();
        }
      }
      // Keep consumer types stable; native confidence is retained, never synthesized.
      normalized[key] = answer.type === 'choice'
        ? { type: 'choice', choice: answer.choice, probabilities: answer.probabilities, ...(answer.confidence !== undefined ? { confidence: answer.confidence } : {}) }
        : { type: 'score', score: answer.score, probabilities: answer.probabilities, ...(answer.confidence !== undefined ? { confidence: answer.confidence } : {}) };
    }
    return { model: parsed.model, answers: normalized };
  } catch {
    throw new Error('Jev evaluation is unavailable: invalid typed response.');
  }
}
